import {useAssortmentsStore} from "~/stores/assortments";
import {queryClient} from "~/plugins/vue-query";
import {clear as clearIDB} from "idb-keyval";
import {useContractTypeTabs} from "~/use/ui/contractTypeTabs";
import {useQueueSizeStore} from "~/use/ui/queueSize";
import {useDraftsDb} from "~/use/indexedDb/draftsDb";
import {useQueueDb} from "~/use/indexedDb/queueDb";
import {useCacheDb} from "~/use/indexedDb/cacheDb";
import {useAxiosClient} from "~/use/axios/client";
import {ContractApi} from "~/gen/openapi/sblService";
import {isDraft} from "~/helpers/contracts";

export const getStorageQuota = async (): Promise<{availableSpace?: number, quota?: number, usage?: number}> => {
    if (navigator.storage && navigator.storage.estimate) {
        navigator.storage.estimate().then(async (estimate) => {
            if (!(estimate.quota && estimate.usage)) {
                return {
                    availableSpace: undefined,
                    quota: undefined,
                    usage: undefined
                }
            }
            console.log(`Storage quota: ${estimate.quota}, usage:  ${estimate.usage}, free: ${(estimate.quota - estimate.usage)}`);
            return {
                availableSpace: (estimate?.quota) - (estimate?.usage || 0),
                quota: estimate.quota,
                usage: estimate.usage,
            }
        }).catch((error) => {
            console.error('Failed to estimate storage:', error);
        });
    } else {
        console.warn('navigator.storage.estimate is not supported in this browser.');
    }
    return {
        availableSpace: undefined,
        quota: undefined,
        usage: undefined,
    }
}

export const cleanUpDrafts = async (): Promise<void> => {
    const { draftsDb } = useDraftsDb()
    const drafts = await draftsDb.getAll()
    const { axiosClient } = useAxiosClient()
    const contractApi = new ContractApi(undefined, '', axiosClient.value)

    const results = await Promise.allSettled(drafts
        .filter(draft => isDraft(draft))
        .map(async draft => {
            const synced = (await contractApi.contractContractWithSlbAppIdExist(draft.Id!))?.data
            return {
                Id: draft.Id,
                synced
            }
        })
    )

    // For all fulfilled requests, loop through and if there is a contract with the temp id
    // delete it from the drafts db
    results
        .filter(({ status }) => status === 'fulfilled')
        .forEach((result: PromiseSettledResult<{id: string, synched: boolean}>) => {
            const { id, synced } = (result as PromiseFulfilledResult<any>).value
            if (synced) {
                draftsDb.delete(id)
            }
        })
}

export const clearData = async (): Promise<void> => {
    const { resetActiveTab } = useContractTypeTabs()
    const { resetQueueSize } = useQueueSizeStore()

    const { draftsDb } = useDraftsDb()
    const { queueDb } = useQueueDb()
    const { cacheDb } = useCacheDb()

    const { clearFavoriteAssortments } = useAssortmentsStore()
    queryClient.clear()

    clearFavoriteAssortments()
    resetActiveTab()
    resetQueueSize()

    localStorage.clear()
    await clearIDB()
    await cacheDb.clear()
    await draftsDb.clear()
    await queueDb.clear()
}